import { isMobile } from "@/js/helpers/mediaQueryHelper";
import { applyFontContrast } from "@/js/helpers/fontContrastHelper";

const CALENDAR_CLASSNAME = ".js-schedule-calendar";
const CALENDAR_PRELOADER_ELEMENT_CLASSNAME = ".js-schedule-calendar-preloader";
const CALENDAR_JSON_ELEMENT_CLASSNAME = ".js-schedule-calendar-json-data";
const CALENDAR_DEFAULT_FILL_PLAN_HEIGHT = 33;
const CALENDAR_CELL_MIN_HEIGHT = 175;
const CALENDAR_MAX_FILL_SPAN = 30;

class ScheduleCalendar {
    fillPlanHeight = CALENDAR_DEFAULT_FILL_PLAN_HEIGHT;

    storeScheduleHeight = {};

    storeHeight = [];

    dataMap = {};

    static attach() {
        const scheduleCalendarElements = document.querySelectorAll(CALENDAR_CLASSNAME);
        if (scheduleCalendarElements) {
            [...scheduleCalendarElements].forEach((scheduleCalendarElement) => {
                /* eslint-disable-next-line no-unused-vars */
                const scheduleCalendar = new ScheduleCalendar(scheduleCalendarElement);
            });
        }
    }

    constructor(calendarElement) {
        this.calendarElement = calendarElement;
        this.calendarPreloaderElement = this.calendarElement.querySelector(
            CALENDAR_PRELOADER_ELEMENT_CLASSNAME
        );
        const calendarJsonElement = this.calendarElement.querySelector(
            CALENDAR_JSON_ELEMENT_CLASSNAME
        );
        const { scheduleCalendarData } = calendarJsonElement.dataset;
        this.calendarData = JSON.parse(scheduleCalendarData);

        this.initiateScheduling();

        window.addEventListener("resize", () => {
            if (isMobile()) {
                this.clearRowStyles();
            } else {
                this.initiateScheduling();
            }
        });
    }

    displayPreloader() {
        if (!isMobile()) {
            this.calendarPreloaderElement.style.display = "flex";
        }
    }

    hidePreloader() {
        this.calendarPreloaderElement.style.display = "none";
    }

    clearRowStyles() {
        const calendarRowElements = this.calendarElement.querySelectorAll(".calendar-dates__row");
        [...calendarRowElements].forEach((calendarRowElement) => {
            calendarRowElement.removeAttribute("style");
        });
    }

    initiateScheduling() {
        const compareScheduleHeightData = this.compareScheduleHeight();
        if (compareScheduleHeightData) {
            this.displayPreloader();
            this.storeHeight = [];

            this.calendarData.forEach((scheduleRowData, scheduleRowDataCount) => {
                this.dataMap = {};
                this.storeScheduleHeight = {};
                const calendarDatesBeltElement = this.calendarElement.querySelector(
                    `.calendar-dates__belt[data-row-id="row${scheduleRowDataCount}"]`
                );
                if (calendarDatesBeltElement) {
                    calendarDatesBeltElement.innerHTML = "";
                    scheduleRowData.forEach((scheduleData) => {
                        this.sortingSchedule(scheduleData, calendarDatesBeltElement);
                    });
                    this.calculateCalendarRowHeight(calendarDatesBeltElement);
                    this.storeHeight.push(this.storeScheduleHeight);
                }
            });

            this.applyEventColor();
            this.hidePreloader();
        }
    }

    compareScheduleHeight() {
        let statusData = false;
        let countLoop = 0;

        if (this.storeHeight.length > 0) {
            const calendarDatesRowElements =
                this.calendarElement.querySelectorAll(".calendar-dates__row");
            if (calendarDatesRowElements) {
                [...calendarDatesRowElements].forEach((calendarDatesRowElement) => {
                    const calendarDatesBeltElement =
                        calendarDatesRowElement.querySelector(".calendar-dates__belt");
                    if (calendarDatesBeltElement) {
                        const calendarDatesBeltItemElements =
                            calendarDatesBeltElement.querySelectorAll(
                                ".calendar-dates__belt__item"
                            );
                        [...calendarDatesBeltItemElements].forEach(
                            (calendarDatesBeltItemElement) => {
                                const elementId = calendarDatesBeltItemElement.id;
                                const findingHeight = this.storeHeight[countLoop][elementId];

                                const elementItem =
                                    calendarDatesBeltElement.querySelector(".event-item");
                                const tempHeight = parseInt(elementItem.offsetHeight, 10);
                                elementItem.removeAttribute("style");
                                elementItem.style.height = `${findingHeight}px`;

                                const tempFillPlan = Math.ceil(tempHeight / this.fillPlanHeight);
                                const findingFillPlan = Math.ceil(
                                    findingHeight / this.fillPlanHeight
                                );
                                if (findingFillPlan !== tempFillPlan) {
                                    statusData = true;
                                }
                            }
                        );
                    }
                    countLoop += 1;
                });
            }
        } else {
            statusData = true;
        }

        return statusData;
    }

    sortingSchedule(scheduleData, calendarDatesBeltElement) {
        /* eslint-disable no-param-reassign */
        let selectedFillPlan = this.checkColliding(scheduleData, 0);

        const eventItemElement = document.createElement("a");
        eventItemElement.className = "event-item";
        eventItemElement.href = scheduleData.link;
        eventItemElement.textContent = scheduleData.text;
        if (scheduleData.type) {
            eventItemElement.dataset.eventTag = scheduleData.type;
        }
        if (scheduleData["bg-color"]) {
            eventItemElement.dataset.eventBgColor = scheduleData["bg-color"];
        }
        if (scheduleData.target === "_blank") {
            eventItemElement.setAttribute("target", "_blank");
            eventItemElement.setAttribute("rel", "noopener noreferrer");
        }

        const eventBoxElement = document.createElement("div");
        eventBoxElement.className = "calendar-dates__belt__item event-box";
        eventBoxElement.setAttribute("data-event-box-id", `schedule${scheduleData.id}`);
        eventBoxElement.setAttribute("fill-plan", selectedFillPlan);
        eventBoxElement.setAttribute("start-span", scheduleData["start-span"]);
        eventBoxElement.setAttribute("date-span", scheduleData["date-span"]);
        eventBoxElement.style.position = "absolute";
        eventBoxElement.appendChild(eventItemElement);

        calendarDatesBeltElement.append(eventBoxElement);

        const selectedElement = calendarDatesBeltElement.querySelector(
            `.event-box[data-event-box-id="schedule${scheduleData.id}"]`
        );
        if (selectedElement) {
            const scheduleHeight = selectedElement.offsetHeight;
            const expectedFillPlan = Math.ceil(scheduleHeight / this.fillPlanHeight);
            scheduleData.row = expectedFillPlan;

            if (expectedFillPlan > 1) {
                const calculatedFillPlan = this.checkColliding(scheduleData, expectedFillPlan);
                if (calculatedFillPlan !== selectedFillPlan) {
                    selectedElement.setAttribute("fill-plan", calculatedFillPlan);
                    selectedFillPlan = calculatedFillPlan;
                }
            }
            scheduleData["fill-plan"] = selectedFillPlan;
            this.schedulePosition(scheduleData, scheduleData.id);

            const calculatedHeight = expectedFillPlan * this.fillPlanHeight - 5;
            selectedElement.querySelector(".event-item").style.height = `${calculatedHeight}px`;
            this.storeScheduleHeight[`schedule${scheduleData.id}`] = calculatedHeight;
        }
        /* eslint-enable no-param-reassign */
    }

    checkColliding(data, expectedFillPlan) {
        let fillPlan = 0;

        for (let countStartSpan = 0; countStartSpan < CALENDAR_MAX_FILL_SPAN; countStartSpan += 1) {
            let selectedStartSpan = data["start-span"];
            let gotCollide = false;

            for (let countDateSpan = 0; countDateSpan < data["date-span"]; countDateSpan += 1) {
                for (let countFillPlan = 0; countFillPlan <= expectedFillPlan; countFillPlan += 1) {
                    if (this.dataMap[`${countStartSpan + countFillPlan}-${selectedStartSpan}`]) {
                        gotCollide = true;
                    }
                }
                selectedStartSpan += 1;
            }

            if (gotCollide === false) {
                fillPlan = countStartSpan;
                break;
            }
        }

        return fillPlan;
    }

    schedulePosition(data, scheduleId) {
        let xAxis = data["fill-plan"];

        for (let count = 0; count < data.row; count += 1) {
            let yAxis = data["start-span"];

            for (let countDateSpan = 0; countDateSpan < data["date-span"]; countDateSpan += 1) {
                this.dataMap[`${xAxis}-${yAxis}`] = scheduleId;
                yAxis += 1;
            }
            xAxis += 1;
        }
    }

    calculateCalendarRowHeight(beltElement) {
        const targetRowElement = beltElement.closest(".calendar-dates__row");
        const cellDateElement = this.calendarElement.querySelector(
            ".calendar-dates__cell .date-number"
        );
        const cellDateHeight = cellDateElement.offsetHeight + 10;

        if (!isMobile()) {
            let highestRow = 0;

            Object.keys(this.dataMap).forEach((key) => {
                const res = key.split("-");
                const compareRowHeight = +res[0];
                if (highestRow < compareRowHeight) {
                    highestRow = compareRowHeight;
                }
            });

            const calendarHeight = (highestRow + 1) * this.fillPlanHeight + cellDateHeight;
            if (calendarHeight > CALENDAR_CELL_MIN_HEIGHT) {
                targetRowElement.style.height = `${calendarHeight}px`;
            }
        } else {
            targetRowElement.style.removeProperty("height");
        }
    }

    applyEventColor() {
        const eventItems = this.calendarElement.querySelectorAll(".event-item");
        [...eventItems].forEach((eventItem) => {
            /* eslint-disable no-param-reassign */
            const { eventBgColor } = eventItem.dataset;
            if (eventBgColor) {
                eventItem.style.backgroundColor = eventBgColor;
                eventItem.classList.add("js-font-contrast");
                applyFontContrast(eventItem);
            }
            /* eslint-enable no-param-reassign */
        });
    }
}

export { ScheduleCalendar };
